import React, { useMemo } from 'react';

import { Divider, Stack, Typography } from '@mui/material';

import CompactChange from './CompactChange';
import FullChangeCard from './FullChangeCard';
import TechnicalDetails from './TechnicalDetails';
import { UNCHANGED_SUMMARY } from './constants';
import { createDisplayChange } from './createDisplayChange';
import { getChangedFields, getProposedFields } from './getChangedFields';

interface Props {
  previous: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
  compact?: boolean;
}

export default function ValueDiff({ previous, proposed, compact }: Props) {
  const changed = useMemo(() => getChangedFields(previous, proposed), [previous, proposed]);
  const displayChanges = useMemo(() => {
    const fields = changed.length === 0 ? getProposedFields(previous, proposed) : changed;
    return fields.map(createDisplayChange);
  }, [changed, previous, proposed]);

  if (displayChanges.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {UNCHANGED_SUMMARY}
      </Typography>
    );
  }

  if (compact) {
    return (
      <>
        {displayChanges.map((change) => (
          <CompactChange key={change.key} change={change} />
        ))}
      </>
    );
  }

  return (
    <Stack spacing={1.5}>
      {displayChanges.map((change) => (
        <FullChangeCard key={change.key} change={change} showPrevious={previous !== null} />
      ))}
      <Divider />
      <TechnicalDetails previous={previous} proposed={proposed} />
    </Stack>
  );
}
